/**
 * Student E2EE session store (Phase 2).
 *
 * Holds the student's UNWRAPPED identity keypair + any decrypted media keys for
 * the lifetime of the browser session. The plaintext private key lives ONLY in
 * this module-level state — it is NEVER persisted to storage and NEVER sent to
 * the server (the server only ever holds the password-wrapped ciphertext).
 *
 * Provisioning flow (called once at login with the student's password):
 *   1. GET /me/e2ee/identity.
 *   2. If absent → buildIdentitySetup(password) + PUT /me/e2ee/identity, cache
 *      the fresh keypair in memory.
 *   3. If present → unwrapPrivateKey(password) using the stored kdf params and
 *      cache the resulting keypair. If unwrap FAILS (e.g. after an admin
 *      password reset rotated the wrapping key — see docs/e2ee.md:102-112) we do
 *      NOT attempt recovery; we provision a FRESH identity so the student can
 *      continue (old shared keys become unreadable until re-shared).
 *
 * Shared-key loading (on login + after each QR scan):
 *   GET /me/e2ee/keys → decrypt each `key_ciphertext` with the session keypair
 *   (sealed-box open) → expose the plaintext media key keyed by key_id so Phase
 *   4 (LiveKit frame encryption) can install it via the KeyProvider.
 */
import {
  getE2eeIdentity,
  setE2eeIdentity,
  listE2eeKeys,
  getE2eeRecovery,
  setE2eeRecovery,
} from "./api";
import {
  whenReady,
  buildIdentitySetup,
  unwrapPrivateKey,
  wrapPrivateKey,
  defaultKdfParams,
  decryptWithPrivateKey,
  generateRecoveryCode,
  normalizeRecoveryCode,
  publicKeyFromPrivate,
} from "./e2ee";
import type { KdfParams } from "@diy-avatar/shared/e2ee";

/** The in-memory, unwrapped identity. Never persisted. */
interface SessionIdentity {
  publicKey: string;
  /** Plaintext X25519 private key (base64url). In-memory ONLY. */
  privateKey: string;
}

interface SessionState {
  identity: SessionIdentity | null;
  /**
   * Decrypted media keys keyed by `<scope>:<scope_id>` (base64url material).
   * Phase 4 decodes it to an ArrayBuffer and installs it into LiveKit's
   * KeyProvider.
   */
  mediaKeys: Map<string, string>;
  /**
   * Phase 6: the key_id of the stored media key, keyed by the same
   * `<scope>:<scope_id>`. Lets the UI compare the negotiated session key_id
   * against what we actually hold to detect a rotation (re-scan needed).
   */
  mediaKeyIds: Map<string, string>;
  /**
   * True when the stored identity could NOT be unwrapped with the login password
   * (rotated wrapping key / admin reset) BUT a recovery blob exists. In that
   * case we deliberately do NOT overwrite the server identity with a fresh one —
   * the original is still recoverable — and the UI prompts for the recovery
   * code. Cleared once an identity is loaded/restored/freshly provisioned.
   */
  recoveryNeeded: boolean;
}

const state: SessionState = {
  identity: null,
  mediaKeys: new Map(),
  mediaKeyIds: new Map(),
  recoveryNeeded: false,
};

/**
 * Default E2EE scope of the kiosk's device key (Phase 4). MUST match the device
 * QR payload (E2eePanel uses scope "device", scope_id 0). The subscriber looks
 * the media key up by `<scope>:<scope_id>` so these constants pin the lookup key
 * on both ends.
 */
export const DEVICE_KEY_SCOPE = "device" as const;
export const DEVICE_KEY_SCOPE_ID = 0;

/** The student's X25519 public key, or null until provisioned. */
export function getMyPublicKey(): string | null {
  return state.identity?.publicKey ?? null;
}

/** True once the in-memory identity has been loaded/created this session. */
export function hasIdentity(): boolean {
  return state.identity !== null;
}

/**
 * True when login could not unlock the stored identity but a recovery blob
 * exists, so the UI should prompt the student to restore with their recovery
 * code instead of silently losing access to previously-shared keys.
 */
export function isRecoveryNeeded(): boolean {
  return state.recoveryNeeded;
}

/**
 * The decrypted media keys, keyed by key_id. Returned as a fresh Map snapshot
 * so callers can't mutate the session store. Consumed by Phase 4.
 */
export function getMediaKeys(): Map<string, string> {
  return new Map(state.mediaKeys);
}

/**
 * The key_id of the stored media key for `<scope>:<scopeId>`, or null when no
 * key is held for that scope. Phase 6: compared against the negotiated session
 * key_id to detect a rotation.
 */
export function getMediaKeyId(scope: string, scopeId: number): string | null {
  return state.mediaKeyIds.get(`${scope}:${scopeId}`) ?? null;
}

/** Clear all in-memory E2EE material (e.g. on logout). */
export function resetE2eeSession(): void {
  state.identity = null;
  state.mediaKeys.clear();
  state.mediaKeyIds.clear();
  state.recoveryNeeded = false;
}

/** Parse stored kdf params JSON, or fall back to fresh defaults. */
function parseKdf(raw: string | null): KdfParams {
  if (raw) {
    try {
      const k = JSON.parse(raw) as KdfParams;
      if (k && k.kdf === "argon2id") return k;
    } catch {
      // fall through
    }
  }
  return defaultKdfParams();
}

/** Whether the server holds a recovery blob for the caller (fail-safe false). */
async function recoveryExists(): Promise<boolean> {
  try {
    const res = await getE2eeRecovery();
    return res.recovery.recovery_wrapped != null;
  } catch {
    return false;
  }
}

/**
 * Provision (or load) the student's identity for this session.
 *
 * Generates a fresh identity on first run; otherwise unwraps the stored private
 * key with `password`. On unwrap failure (rotated wrapping key / admin reset):
 *   - if a RECOVERY blob exists, the stored identity is left UNTOUCHED and
 *     `recoveryNeeded` is flagged so the UI can prompt for the recovery code
 *     (which restores the original identity + previously-shared keys), and this
 *     returns null;
 *   - otherwise a fresh identity is provisioned so the student can keep using
 *     E2EE (old shared keys stay unreadable until re-shared).
 *
 * Returns the public key on success, or null when recovery is required. Throws
 * only on hard network failures of the PUT.
 */
export async function provisionIdentity(password: string): Promise<string | null> {
  await whenReady();
  state.recoveryNeeded = false;

  let res: Awaited<ReturnType<typeof getE2eeIdentity>>;
  try {
    res = await getE2eeIdentity();
  } catch {
    // Identity GET failed (e.g. flag off or transient) — create a fresh one.
    return await createAndStoreFreshIdentity(password);
  }

  const id = res.identity;
  if (!id.public_key || !id.privkey_wrapped) {
    // No identity yet → first-time setup.
    return await createAndStoreFreshIdentity(password);
  }

  const kdf = parseKdf(id.kdf_params);
  const priv = unwrapPrivateKey(id.privkey_wrapped, password, kdf);
  if (priv === null) {
    // Wrong password OR rotated wrapping key (admin reset). Before destroying the
    // stored identity, check whether a recovery blob can bring it back.
    if (await recoveryExists()) {
      // Leave the server identity intact; the UI prompts for the recovery code.
      state.identity = null;
      state.recoveryNeeded = true;
      return null;
    }
    // No recovery available → provision a fresh identity (old keys orphaned).
    return await createAndStoreFreshIdentity(password);
  }

  state.identity = { publicKey: id.public_key, privateKey: priv };
  return id.public_key;
}

/**
 * Generate a fresh identity, PUT it, and cache it in memory.
 *
 * A fresh keypair makes any existing recovery blob STALE: that blob still wraps
 * the OLD private key, so a recovery code would "succeed" yet restore an
 * identity that no longer matches the server pubkey (silent orphaning). We
 * therefore clear the recovery blob whenever a fresh identity is genuinely
 * minted, so a stale recovery code can never appear valid against a rotated key.
 * The student is prompted to set up recovery afresh for the new identity.
 */
async function createAndStoreFreshIdentity(password: string): Promise<string> {
  const setup = buildIdentitySetup(password);
  await setE2eeIdentity(setup.request);
  state.identity = {
    publicKey: setup.identity.publicKey,
    privateKey: setup.identity.privateKey,
  };
  // A fresh identity supersedes any recovery-needed state.
  state.recoveryNeeded = false;
  // Any previously-decrypted media keys belonged to the old identity.
  state.mediaKeys.clear();
  state.mediaKeyIds.clear();
  // The old recovery blob wraps the now-superseded key → clear it so a stale
  // recovery code can never appear valid against the freshly-minted identity.
  try {
    await disableRecovery();
  } catch {
    // Best-effort: a failed clear leaves a stale blob, but recoverWithCode
    // reconstructs the pubkey from the recovered key, so a mismatch is visible
    // rather than silently accepted.
  }
  return setup.identity.publicKey;
}

/**
 * Load all shared keys and decrypt them with the session keypair, populating the
 * in-memory media-key map (keyed by key_id). Best-effort: undecryptable entries
 * (e.g. sealed to a previous identity) are skipped. No-op without an identity.
 *
 * NOTE: `SharedKeyEntry` has no key_id column — entries are keyed by
 * `scope:scope_id` as the map key fallback so callers can find them. Phase 4
 * can re-key by the device's key_id once frame encryption is wired.
 */
export async function loadSharedKeys(): Promise<Map<string, string>> {
  if (!state.identity) return getMediaKeys();
  let res: Awaited<ReturnType<typeof listE2eeKeys>>;
  try {
    res = await listE2eeKeys();
  } catch {
    return getMediaKeys();
  }
  for (const entry of res.keys) {
    if (!entry.key_ciphertext) continue;
    const plain = decryptWithPrivateKey(
      entry.key_ciphertext,
      state.identity.publicKey,
      state.identity.privateKey,
    );
    if (plain === null) continue; // sealed to a different identity → skip
    const k = `${entry.scope}:${entry.scope_id}`;
    state.mediaKeys.set(k, plain);
    // Phase 6: track the stored key's id (when the server has one) so the UI can
    // detect a rotation by comparing against the negotiated session key_id.
    if (entry.key_id) state.mediaKeyIds.set(k, entry.key_id);
    else state.mediaKeyIds.delete(k);
  }
  return getMediaKeys();
}

/**
 * Record a freshly-scanned media key in the session store immediately (so it is
 * usable without a round-trip back through GET /me/e2ee/keys). Keyed the same
 * way as `loadSharedKeys`.
 */
export function rememberMediaKey(
  scope: string,
  scopeId: number,
  keyMaterial: string,
  keyId?: string,
): void {
  const k = `${scope}:${scopeId}`;
  state.mediaKeys.set(k, keyMaterial);
  // Phase 6: record the key_id of the just-scanned key so a subsequent stale
  // check against the negotiated session key_id resolves immediately.
  if (keyId) state.mediaKeyIds.set(k, keyId);
}

/**
 * Re-wrap the in-memory private key with a NEW password and persist it via PUT
 * /me/e2ee/identity. Call this from the student's password-change handler AFTER
 * the password is successfully changed server-side.
 *
 * Requires the identity to be loaded in memory (it is, post-login). When the
 * in-memory key is absent it falls back to unwrapping with `oldPw` from the
 * server copy first.
 *
 * CRITICAL (Part C): when a server identity EXISTS but the key cannot be
 * recovered for re-wrapping (no in-memory key AND `oldPw` does not unwrap the
 * server copy), this throws the typed `e2ee_rewrap_failed` and does NOT mint a
 * fresh identity. Minting a fresh keypair here would overwrite the still-valid
 * server identity while the recovery blob continues to wrap the OLD key — a
 * silent orphaning that breaks recovery-by-code. The caller must surface this so
 * the student can restore via their recovery code (or re-add the key) instead of
 * a "successful" password change that quietly decoupled E2EE.
 */
export async function rewrapIdentityForNewPassword(
  oldPw: string,
  newPw: string,
): Promise<void> {
  await whenReady();

  let identity = state.identity;
  if (!identity) {
    // Not loaded this session — pull + unwrap with the old password.
    const res = await getE2eeIdentity();
    const id = res.identity;
    if (!id.public_key || !id.privkey_wrapped) {
      // No server identity at all → nothing existing to orphan. Provision fresh
      // under the NEW password (createAndStoreFreshIdentity clears any blob).
      await createAndStoreFreshIdentity(newPw);
      return;
    }
    const kdf = parseKdf(id.kdf_params);
    const priv = unwrapPrivateKey(id.privkey_wrapped, oldPw, kdf);
    if (priv === null) {
      // A server identity is present but unreadable with this old password.
      // Do NOT mint a fresh identity here — that would orphan the recovery blob
      // and lose the key. Surface a typed error for the caller to handle.
      throw new Error("e2ee_rewrap_failed");
    }
    identity = { publicKey: id.public_key, privateKey: priv };
    state.identity = identity;
  }

  const kdf = defaultKdfParams();
  const wrapped = wrapPrivateKey(identity.privateKey, newPw, kdf);
  await setE2eeIdentity({
    public_key: identity.publicKey,
    privkey_wrapped: wrapped,
    kdf_params: JSON.stringify(kdf),
  });
}

// ---- Recovery flow ----
//
// A RECOVERY CODE lets the student restore their identity (and thus decrypt
// previously-shared media keys) after a password change / admin reset rotated
// the password-derived wrapping key. We store a SECOND wrapped copy of the
// private key, this time wrapped with a key derived from the recovery code, in
// `users.e2ee_recovery_*` (opaque server-side). The plaintext code is shown to
// the student ONCE and never persisted server-side.

/**
 * Set up (or rotate) recovery: generate a fresh recovery code, wrap the in-memory
 * private key with it, and persist the opaque blob via PUT /me/e2ee/recovery.
 * Returns the human-formatted recovery code to show the student ONCE.
 *
 * Requires the identity to be loaded this session (it is, post-login). Throws
 * "e2ee_no_identity" when no identity is in memory so the caller can surface it.
 */
export async function setupRecovery(): Promise<string> {
  await whenReady();
  if (!state.identity) throw new Error("e2ee_no_identity");
  const code = generateRecoveryCode();
  const kdf = defaultKdfParams();
  const wrapped = wrapPrivateKey(
    state.identity.privateKey,
    normalizeRecoveryCode(code),
    kdf,
  );
  await setE2eeRecovery({
    recovery_wrapped: wrapped,
    recovery_kdf_params: JSON.stringify(kdf),
  });
  return code;
}

/** Disable recovery (clear the stored recovery blob). */
export async function disableRecovery(): Promise<void> {
  await setE2eeRecovery({ recovery_wrapped: null, recovery_kdf_params: null });
}

/**
 * Decision-3 gate helper for a self-service password change.
 *
 * Reports whether an upcoming `rewrapIdentityForNewPassword(oldPw, …)` can
 * preserve the existing E2EE key, so the caller can decide whether to confirm
 * (preservable) or hard-require a recovery key first (would-destroy).
 *
 * Returns:
 *   - "preserve": the in-memory identity is present, OR `oldPw` unwraps the
 *     server copy → the re-wrap will carry the key over.
 *   - "fresh": no server identity exists at all → the re-wrap mints a brand-new
 *     identity; nothing existing is destroyed (no recovery gate needed).
 *   - "destroy": a server identity exists but cannot be preserved (no in-memory
 *     key AND `oldPw` does not unwrap it) → the change would orphan the key, so
 *     the caller must require a recovery key first (or block).
 *
 * Fail-safe: on a network error reading the identity it returns "preserve" only
 * when an in-memory key exists, else "destroy" (treat unknown as risky).
 */
export async function classifyRewrap(
  oldPw: string,
): Promise<"preserve" | "fresh" | "destroy"> {
  await whenReady();
  if (state.identity) return "preserve";
  let res: Awaited<ReturnType<typeof getE2eeIdentity>>;
  try {
    res = await getE2eeIdentity();
  } catch {
    return "destroy";
  }
  const id = res.identity;
  if (!id.public_key || !id.privkey_wrapped) return "fresh";
  const kdf = parseKdf(id.kdf_params);
  const priv = unwrapPrivateKey(id.privkey_wrapped, oldPw, kdf);
  return priv === null ? "destroy" : "preserve";
}

/** Whether a recovery blob is stored server-side for the caller. */
export async function hasRecovery(): Promise<boolean> {
  try {
    const res = await getE2eeRecovery();
    return res.recovery.recovery_wrapped != null;
  } catch {
    return false;
  }
}

/**
 * Restore the identity from a recovery code, then re-wrap the recovered private
 * key under `newPassword` (so password-based unwrap works again) and persist it
 * via PUT /me/e2ee/identity. Loads the now-decryptable shared keys.
 *
 * Returns true on success; false when no recovery blob exists or the code is
 * wrong (unwrap failed). The public key is reconstructed from the recovered
 * private key, so the restored identity matches the keys originally sealed to it.
 */
export async function recoverWithCode(
  code: string,
  newPassword: string,
): Promise<boolean> {
  await whenReady();
  let rec: Awaited<ReturnType<typeof getE2eeRecovery>>;
  try {
    rec = await getE2eeRecovery();
  } catch {
    return false;
  }
  const wrapped = rec.recovery.recovery_wrapped;
  if (!wrapped) return false;
  const kdf = parseKdf(rec.recovery.recovery_kdf_params);
  const priv = unwrapPrivateKey(wrapped, normalizeRecoveryCode(code), kdf);
  if (priv === null) return false;
  const pub = publicKeyFromPrivate(priv);
  if (pub === null) return false;

  state.identity = { publicKey: pub, privateKey: priv };
  state.recoveryNeeded = false;
  // Re-wrap under the new password so future logins unwrap normally.
  const newKdf = defaultKdfParams();
  const reWrapped = wrapPrivateKey(priv, newPassword, newKdf);
  await setE2eeIdentity({
    public_key: pub,
    privkey_wrapped: reWrapped,
    kdf_params: JSON.stringify(newKdf),
  });
  // Now that the original identity is back, previously-sealed keys decrypt.
  await loadSharedKeys();
  return true;
}

/** Test-only accessor for the internal state. */
export const __test = { state };
